import { useState, useCallback } from 'react';
import Lightbox from '@/components/base/Lightbox';

const allianceImages = [
  {
    src: "https://readdy.ai/api/search-image?query=Industrial%20partnership%20concept%20between%20China%20and%20Venezuela%2C%20split%20composition%20showing%20modern%20chemical%20factory%20on%20one%20side%20and%20oil%20rig%20silhouette%20on%20the%20other%2C%20connected%20by%20abstract%20bridge%20or%20shipping%20route%2C%20warm%20amber%20and%20deep%20blue%20color%20palette%2C%20corporate%20professional%20style%2C%20editorial%20illustration&width=800&height=600&seq=alliance-partnership&orientation=landscape",
    alt: "Alianza estratégica entre Rixin Corp China y operaciones petroleras en Venezuela",
    caption: "Alianza China-Venezuela",
  },
];

export default function AboutSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = useCallback(() => setLightboxIndex(0), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  return (
    <section id="about" className="bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">

          {/* ========== ¿Quiénes Somos? ========== */}
          <div className="mb-20 md:mb-28 -mx-4 md:-mx-8 lg:-mx-16">
            <div className="bg-background-100">
              <div className="w-full px-4 md:px-8 lg:px-16">
                <div className="max-w-7xl mx-auto py-12 md:py-16">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Video */}
                    <div className="relative">
                      <div className="relative rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
                        <video
                          src="https://storage.helloreaddy.io/project_files/9755469e-cfe9-4755-b8ae-44d6e19e4537/8f5e6d19-c60f-4592-8ab9-66e6e0765ded_DbjIiFF.mp4"
                          controls
                          playsInline
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-6">
                        <i className="ri-building-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        ¿Quiénes Somos?
                      </div>

                      <h2 className="font-heading text-xl sm:text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4 md:mb-6">
                        Importadores de Demulsificantes{' '}
                        <span className="text-primary-500">De Rixin Corp, China</span>
                      </h2>

                      <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5 text-left sm:text-justify">
                        En <strong className="text-foreground-950">RESINVENCA RESINAS DE VENEZUELA C.A.</strong> somos su puente comercial entre China y Venezuela. 
                        Importamos demulsificantes ya formulados y producidos por <strong className="text-foreground-950">Rixin Corp</strong>, 
                        uno de los principales fabricantes de productos químicos para la industria petrolera en Asia.
                      </p>

                      <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 text-left sm:text-justify">
                        El producto llega a Venezuela listo para usar: formulado, certificado y empacado. 
                        Nos encargamos de toda la logística internacional, aduana y entrega final a sus operaciones 
                        en la Faja Petrolífera del Orinoco. Usted recibe demulsificante de alta calidad sin complicaciones.
                      </p>

                      <div className="grid grid-cols-2 xs:grid-cols-2 gap-4">
                        {[
                          { icon: 'ri-ship-line', text: 'Importación Directa desde China' },
                          { icon: 'ri-drop-line', text: 'Producto Ya Formulado y Listo' },
                          { icon: 'ri-oil-line', text: 'Todos los Campos Petroleros' },
                          { icon: 'ri-shield-check-line', text: 'Aprobado por Laboratorios PDVSA' },
                        ].map((item) => (
                          <div key={item.text} className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                              <i className={`${item.icon} text-sm`}></i>
                            </div>
                            <span className="text-foreground-700 text-sm font-medium truncate">{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ========== Nuestra Alianza Estratégica ========== */}
          <div className="bg-background-100 rounded-lg p-6 md:p-12 mb-20 md:mb-28 border border-background-200/60">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
                  <i className="ri-shake-hands-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  Nuestra Alianza Estratégica
                </div>

                <h3 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight mb-6">
                  Fabricante <span className="text-primary-500">Rixin Corp</span>{' '}
                  + Asesoría <span className="text-accent-500">Poly Alphas</span>
                </h3>

                <p className="text-foreground-600 text-sm md:text-base leading-relaxed text-left sm:text-justify">
                  <strong>RESINVENCA RESINAS DE VENEZUELA C.A.</strong> importa demulsificantes ya formulados de <strong>Rixin Corp (China)</strong> 
                  y, con el respaldo técnico de <strong>Poly Alphas De Venezuela C.A.</strong>, validamos la eficacia 
                  de cada lote en condiciones reales de campo. Este modelo único combina la capacidad 
                  manufacturera asiática con el conocimiento local del crudo venezolano.
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mt-4 text-left sm:text-justify">
                  <strong>Poly Alphas Chemicals C.A.</strong> actúa como nuestro asesor técnico en Venezuela, 
                  realizando pruebas físico-químicas in situ para determinar la dosis óptima y confirmar 
                  la compatibilidad del producto importado con las condiciones específicas de cada campo petrolero.
                </p>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-3 mt-6">
                  {[
                    { icon: 'ri-building-4-line', text: 'Rixin Corp China – Fabricante' },
                    { icon: 'ri-ship-line', text: 'Importación Directa' },
                    { icon: 'ri-microscope-line', text: 'Validación Técnica Local' },
                    { icon: 'ri-test-tube-line', text: 'Pruebas Físico-Químicas' },
                  ].map((item) => (
                    <div key={item.text} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-accent-100/60 text-accent-700 text-xs font-medium border border-accent-200/40">
                      <i className={`${item.icon} w-3 h-3 flex items-center justify-center`}></i>
                      {item.text}
                    </div>
                  ))}
                </div>
              </div>

              {/* Alliance image */}
              <div
                onClick={openLightbox}
                className="group relative rounded-lg overflow-hidden border border-background-200/60 shadow-sm min-h-[200px] sm:min-h-[300px] cursor-pointer"
              >
                <img
                  src={allianceImages[0].src}
                  alt={allianceImages[0].alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-semibold">{allianceImages[0].caption}</p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== Experiencia y Trayectoria ========== */}
          <div>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-6">
                <i className="ri-bar-chart-line text-sm w-4 h-4 flex items-center justify-center"></i>
                Experiencia y Trayectoria
              </div>
              <h3 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                Resultados que{' '}
                <span className="text-primary-500">Hablan por Sí Solos</span>
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Export stat */}
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center border border-background-200/60">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-5">
                  <i className="ri-ship-line text-xl"></i>
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-primary-500 mb-2">200+</div>
                <p className="text-foreground-600 text-sm font-medium mb-2">Toneladas Importadas</p>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Más de 200 toneladas de demulsificante importadas con éxito desde China a Venezuela.
                </p>
              </div>

              {/* Clients stat */}
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center border border-background-200/60">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
                  <i className="ri-building-2-line text-xl"></i>
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-accent-500 mb-2">Diversas</div>
                <p className="text-foreground-600 text-sm font-medium mb-2">Empresas Venezolanas</p>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Abastecemos a empresas que operan mediante contratos de servicio y alianzas estratégicas con PDVSA.
                </p>
              </div>

              {/* Quality stat */}
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center border border-background-200/60">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-5">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-secondary-500 mb-2">Altos</div>
                <p className="text-foreground-600 text-sm font-medium mb-2">Estándares de Calidad</p>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Producto formulado bajo estándares internacionales, validado por laboratorios PDVSA.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Lightbox
        images={allianceImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={closeLightbox}
        onNext={closeLightbox}
      />
    </section>
  );
}